import { Injectable } from '@tanbo/vue-di-plugin'
import { ParamsClean } from '@/common/decorators/params-clean'
import { Http } from '@/services/http'
import type {
  DistributionItem,
  SafeEventDistributionTimeParams,
  SafeEventLevelDistributionRes,
  SafeEventListRequestParams,
  SafeEventListApiResponse,
  SafeEventStatRes,
  SafeEventTypeDistributionRes,
  SecurityEventDetail,
  SecurityEventListItem,
  SecurityEventListQuery,
  SecurityEventListResponse,
  SecurityEventOverview,
} from './types'
import { DEFAULT_PENDING_STATUS, DistributionDimension, SecurityEventLevel } from './constants/enums'
import { mapSafeEventDetailResponse } from './security-event-detail.mapper'
import type {
  SafeEventDetailApiResponse,
  SafeEventDetailProcessChain,
  SafeEventDetailRequestParams,
} from './types/sdk-event-model'
import { toApiHandlingRange } from './handling-request.types'
import type { SafeEventHandlingRequestParams } from './handling-request.types'

export { toApiHandlingRange } from './handling-request.types'
export type { SafeEventHandlingRequestParams } from './handling-request.types'

export function pickBusinessIds (rows: Array<{ business_id: string }>): string[] {
  return rows.map((r) => r.business_id).filter(Boolean)
}

export interface SimpleRequestParams {
  /** 业务id */
  business_id?: string
}

export interface Log {
  /** 处置时间(时间戳-秒) */
  handle_time: string
  /** 操作人用户名 */
  operator: string
  /** 操作内容 */
  content: string
  /** 操作类型: 0-未知, 1-封堵, 2-已处置, 3-忽略, 4-添加信任, 5-隔离, 6-取消信任, 7-取消忽略 */
  operate_type: number
  /** 指令任务id */
  task_id: string
  /** 指令执行状态: 0-预发送; 1-执行中; 2-执行成功; 3-执行失败; 4-发送超时; 5-发送失败; -1-非指令类型或查询不到 */
  status: number
}

export interface SimpleResponseData {
  /** 威胁实体 */
  threat_entity: string
  /** 告警次数 */
  alarm_count?: number
  /** ATT&CK 子技术 */
  attack_subtechnique?: string
  /** ATT&CK 战术 */
  attack_tactic?: string
  /** ATT&CK 技术 */
  attack_technique?: string
  /** 告警事件 */
  event_time?: string
  /** 首次告警时间 */
  first_alarm_time?: string
  /** 处置状态 0-全部, 1-未处置 2-已信任 3-已处置 4-已忽略 */
  handle_status?: number
  /** 最近告警时间 */
  last_alarm_time?: string
  /** 处置时间线 */
  log?: Log[]
  /** 流量方向 0-未知, 1-入向, 2-出向 */
  network_direction?: number
  /** 威胁实体类型:1-文件,2-进程,3-IP4-行为哈希,5-URL,6-用户 */
  threat_type?: number
  event_type: string,
  /** 列表响应：1-低危, 2-中危, 3-高危 */
  event_level: number
  event_id:string
}
export interface ExpandRequestParams {
  /** 设备 id（必填，用于确定分表） */
  device_id: string
  /** 进程 guid -> 节点的pid */
  process_guid: string
  /** 最大层级（默认 3，最大 10） */
  max_level?: number
}

export interface CancelTrustOrIgnoreRequestParams {
  business_ids: string[]
}
export interface ProcessChainResponseData {
  /** 进程链（修改为 ObjectInfo 类型） */
  process_chain?: SafeEventDetailProcessChain[]
}
@Injectable()
export class SecurityEventService {
  constructor (private http: Http) {}

  getSafeeventStat () {
    return this.http.post<SafeEventStatRes>('/safeevent/stat', {})
  }

  async getOverview (): Promise<SecurityEventOverview> {
    const res = await this.getSafeeventStat()
    return {
      new_events_30d: res.new_events_last_30_days ?? 0,
      pending_count: res.pending_events ?? 0,
      trusted_count: res.trusted_events ?? 0,
      ignored_count: res.ignored_events ?? 0,
    }
  }

  async getDistribution (params: {
    dimension: string
    start_time?: number
    end_time?: number
  }): Promise<{ items: DistributionItem[] }> {
    const timeParams: SafeEventDistributionTimeParams = {
      start_time: params.start_time,
      end_time: params.end_time,
    }
    if (params.dimension === DistributionDimension.Level) {
      const res = await this.getLevelDistribution(timeParams)
      return {
        items: [
          { key: SecurityEventLevel.High, count: res.high_level_count ?? 0 },
          { key: SecurityEventLevel.Medium, count: res.mid_level_count ?? 0 },
          { key: SecurityEventLevel.Low, count: res.low_level_count ?? 0 },
        ].filter((item) => item.count > 0),
      }
    }
    if (params.dimension === DistributionDimension.Category) {
      const res = await this.getCategorizationDistribution(timeParams)
      return { items: this.mapTypeDistribution(res) }
    }
    const res = await this.getAttckDistribution(timeParams)
    return { items: this.mapTypeDistribution(res) }
  }

  getSafeEventCancelTrust (data: CancelTrustOrIgnoreRequestParams) {
    return this.http.post<void>('/safeevent/cancel_trust', data)
  }

  getSafeEventCancelIgnore (data: CancelTrustOrIgnoreRequestParams) {
    return this.http.post<void>('/safeevent/cancel_ignore', data)
  }

  getSafeEventHandling (data: SafeEventHandlingRequestParams) {
    return this.http.post<void>('/safeevent/handling', data)
  }

  getLevelDistribution (params: SafeEventDistributionTimeParams) {
    return this.http.post<SafeEventLevelDistributionRes>('/safeevent/level_distribution', params)
  }

  getSafeEventDetail (data: SafeEventDetailRequestParams) {
    return this.http.post<SafeEventDetailApiResponse>('/safeevent/detail', data)
  }

  getAttckDistribution (params: SafeEventDistributionTimeParams) {
    return this.http.post<SafeEventTypeDistributionRes>('/safeevent/attck', params)
  }

  getCategorizationDistribution (params: SafeEventDistributionTimeParams) {
    return this.http.post<SafeEventTypeDistributionRes>('/safeevent/categorization', params)
  }
  // // 获取告警信息
  // getSafeeventDetailSimple (data: SimpleRequestParams) {
  //   return this.http.post<SimpleResponseData>('/safeevent/detail_simple', data)
  // }

  getSafeeventProcessChain (data: SimpleRequestParams) {
    return this.http.post<ProcessChainResponseData>('/safeevent/process_chain', data)
  }
 getSafeeventExpandProcessChain (data: ExpandRequestParams) {
    return this.http.post<ProcessChainResponseData>('/safeevent/expand_process_chain', data)
  }
  private mapTypeDistribution (res: SafeEventTypeDistributionRes): DistributionItem[] {
    const list = res.data ?? res.list ?? []
    return list
      .filter((item) => item.count > 0)
      .map((item) => ({ key: item.data_type, count: item.count }))
  }

  getSafeeventList (data: SafeEventListRequestParams) {
    return this.http.post<SafeEventListApiResponse>('/safeevent/list', data)
  }

  @ParamsClean()
  async getList (params: SecurityEventListQuery): Promise<SecurityEventListResponse> {
    const res = await this.getSafeeventList(this.toListRequest(params))
    return {
      list: res.event_list ?? [],
      total: res.total ?? 0,
    }
  }

  private toListRequest (params: SecurityEventListQuery): SafeEventListRequestParams {
    const { date: _d, keyword, sort_field: _sf, sort_order: _so, ...rest } = params
    const req: SafeEventListRequestParams = { ...rest }
    if (keyword) req.keyword = keyword
    return req
  }

  async getDetail (
    businessId: string,
    base?: Partial<SecurityEventListItem>,
  ): Promise<SecurityEventDetail | undefined> {
    const res = await this.getSafeEventDetail({ business_id: businessId })
    if (!res || typeof res !== 'object') return undefined
    try {
      return mapSafeEventDetailResponse(res, businessId, base)
    } catch (e) {
      console.error('[SecurityEvent] map detail failed', e)
      return undefined
    }
  }

  async cancelIgnore (rows: Array<{ business_id: string }>): Promise<void> {
    const businessIds = pickBusinessIds(rows)
    if (!businessIds.length) return
    await this.getSafeEventCancelIgnore({ business_ids: businessIds })
  }

  async cancelTrust (rows: Array<{ business_id: string }>): Promise<void> {
    const businessIds = pickBusinessIds(rows)
    if (!businessIds.length) return
    await this.getSafeEventCancelTrust({ business_ids: businessIds })
  }

  async handleEvent (params: SafeEventHandlingRequestParams): Promise<void> {
    if (!params.business_id) return
    await this.getSafeEventHandling(params)
  }

  async submitHandling (params: SafeEventHandlingRequestParams): Promise<void> {
    await this.handleEvent(params)
  }

  async markHandled (
    businessId: string,
    range?: number,
    scope?: Pick<SafeEventHandlingRequestParams, 'device_list' | 'group_id'>,
  ): Promise<void> {
    await this.handleEvent({
      business_id: businessId,
      handle_type: 2,
      range,
      device_list: scope?.device_list,
      group_id: scope?.group_id,
    })
  }

  async markIgnored (
    businessId: string,
    range?: number,
    scope?: Pick<SafeEventHandlingRequestParams, 'device_list' | 'group_id'>,
  ): Promise<void> {
    await this.handleEvent({
      business_id: businessId,
      handle_type: 3,
      range,
      device_list: scope?.device_list,
      group_id: scope?.group_id,
    })
  }

  async addEventTrust (
    businessId: string,
    applyScope?: number,
    scope?: Pick<SafeEventHandlingRequestParams, 'device_list' | 'group_id'>,
  ): Promise<void> {
    await this.handleEvent({
      business_id: businessId,
      handle_type: 4,
      range: toApiHandlingRange(applyScope),
      device_list: scope?.device_list,
      group_id: scope?.group_id,
    })
  }

  async blockOrIsolateEvent (params: SafeEventHandlingRequestParams): Promise<void> {
    await this.handleEvent({ ...params, handle_type: 1 })
  }

  async exportList (params: SecurityEventListQuery, fileType: 'csv' | 'xlsx', moduleName = 'security-event') {
    return this.http.downloadWithBlob(
      '/safeevent/export',
      { ...this.toListRequest(params), file_type: fileType === 'csv' ? 0 : 1 },
      moduleName
    )
  }
}

export function buildDefaultListQuery (overrides: Partial<SecurityEventListQuery> = {}): SecurityEventListQuery {
  return {
    page: 1,
    limit: 10,
    handle_status: DEFAULT_PENDING_STATUS,
    group_id: -1,
    sort_field: 'last_alarm_time',
    sort_order: 'desc',
    ...overrides,
  }
}
